import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, ShoppingBagIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { Button } from './Button';
import { SocialIcon } from './SocialIcon';
import { useCart } from '../contexts/CartContext';
import { navLinks, socialLinks } from '../data/site';
export function Header() {
  const {
    count,
    open
  } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname, location.search]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  return <header className={`sticky top-0 z-40 border-b bg-white/90 backdrop-blur-md transition-[border-color] duration-200 ${scrolled || menuOpen ? 'border-stone' : 'border-transparent'}`} data-id="element-225" data-mp="2mmwy66h2nmdo">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:bg-charcoal focus:px-4 focus:py-2 focus:text-white" data-id="element-226" data-mp="3fd6pjixesohh">
        Skip to content
      </a>
      <div className="gutter flex h-[clamp(4rem,3.6rem+1vw,5.25rem)] items-center gap-4" data-id="element-227" data-mp="30mzfnrlgwdse">
        <Link to="/" aria-label="True Twelve home" className="shrink-0" data-id="element-228" data-mp="1w6031p787s6">
          <Logo data-id="element-229" data-mp="1yire5gujuhw" />
        </Link>

        <nav aria-label="Primary" className="hidden flex-1 justify-center gap-[clamp(1.5rem,2.6vw,4rem)] lg:flex" data-id="element-230" data-mp="3taligxmvfee7">
          {navLinks.map(l => <NavLink key={l.to} to={l.to} className={({
          isActive
        }) => `t-label relative py-2 text-charcoal after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:bg-gold after:transition-transform after:duration-200 after:ease-out ${isActive ? 'after:scale-x-100' : 'after:scale-x-0 hover:after:scale-x-100'}`} data-id="element-231" data-mp="1recxrnh0p7no">
              {l.label}
            </NavLink>)}
        </nav>

        <div className="ml-auto flex items-center gap-1 sm:gap-2 lg:ml-0" data-id="element-232" data-mp="yp5p3vnun1op">
          <ul className="mr-2 hidden items-center gap-1 xl:flex" aria-label="Social" data-id="element-233" data-mp="yr0ebm65lfyl">
            {socialLinks.map(s => <li key={s.network} data-id="element-234" data-mp="2t36nygjefoos">
                <a href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} className="grid h-10 w-10 place-items-center text-charcoal transition-colors duration-150 hover:text-gold-deep" data-id="element-235" data-mp="2t48bvllik5nx">
                  <SocialIcon network={s.network} data-id="element-236" data-mp="hh4su8p538vl" />
                </a>
              </li>)}
          </ul>
          <button type="button" onClick={open} aria-label={`Open cart, ${count} item${count === 1 ? '' : 's'}`} className="relative grid h-10 w-10 place-items-center text-charcoal transition-colors duration-150 hover:text-gold-deep" data-id="element-237" data-mp="w5snmcr5vekh">
            <ShoppingBagIcon strokeWidth={1.25} className="h-5 w-5" aria-hidden="true" data-id="element-238" data-mp="105tbeo05h247" />
            {count > 0 && <span className="absolute right-0.5 top-0.5 grid h-4 min-w-[1rem] place-items-center rounded-full bg-charcoal px-1 text-[0.6rem] font-medium text-white" data-id="element-239" data-mp="1f0zfh5foqquw">
                {count}
              </span>}
          </button>
          <Button to="/readings#book" size="sm" className="ml-1 px-4 sm:px-5" data-id="element-240" data-mp="3bwdm9f6iw2dc">
            <span className="sm:hidden" data-id="element-241" data-mp="22z6ucd99s9g6">Book</span>
            <span className="hidden sm:inline" data-id="element-242" data-mp="22yfau0sy6269">Book a Reading</span>
          </Button>
          <button type="button" onClick={() => setMenuOpen(v => !v)} aria-expanded={menuOpen} aria-controls="mobile-menu" aria-label={menuOpen ? 'Close menu' : 'Open menu'} className="grid h-10 w-10 place-items-center text-charcoal lg:hidden" data-id="element-243" data-mp="27wgvur9xobkd">
            {menuOpen ? <XIcon strokeWidth={1.25} className="h-5 w-5" data-id="element-244" data-mp="1p7a6ilemwlpl" /> : <MenuIcon strokeWidth={1.25} className="h-5 w-5" data-id="element-245" data-mp="1p8223w88ejfv" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-246" data-mp="1t7avv139uhbf">
        {menuOpen && <motion.div id="mobile-menu" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="gutter fixed inset-x-0 bottom-0 top-[clamp(4rem,3.6rem+1vw,5.25rem)] overflow-y-auto bg-white pb-10 pt-6 lg:hidden" data-id="element-247" data-mp="1xzc48bxbjnw6">
            <nav aria-label="Mobile" data-id="element-248" data-mp="t0xdkrsjxk5b">
              <ul className="divide-y divide-stone border-y border-stone" data-id="element-249" data-mp="t1yyrp3a7n0r">
                {navLinks.map(l => <li key={l.to} data-id="element-250" data-mp="ox94g4o9ughy">
                    <NavLink to={l.to} className="flex items-baseline gap-5 py-5" data-id="element-251" data-mp="jzzf0p0vu4u4">
                      <span className="t-numeral w-8 text-sm text-gold-deep" data-id="element-252" data-mp="k2bw6zytgb64">{l.numeral}</span>
                      <span className="t-h3 font-light" data-id="element-253" data-mp="f2z86bw7ky1y">{l.label}</span>
                    </NavLink>
                  </li>)}
              </ul>
            </nav>
            <Button to="/readings#book" size="lg" className="mt-8 w-full" data-id="element-254" data-mp="5bmh3mi8ibwz">
              Book a Reading
            </Button>
            <ul className="mt-8 flex gap-2" aria-label="Social" data-id="element-255" data-mp="17oire0qh54z">
              {socialLinks.map(s => <li key={s.network} data-id="element-256" data-mp="3ngpstq2phm6d">
                  <a href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} className="grid h-11 w-11 place-items-center border border-stone text-charcoal" data-id="element-257" data-mp="3nhr49vnmwaom">
                    <SocialIcon network={s.network} data-id="element-258" data-mp="3irx4uh5b05ym" />
                  </a>
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </header>;
}